import { useSuspenseQuery } from "@tanstack/react-query"
import styled from "@emotion/styled"
import { Value } from "@stringtale/react"
import type { GetGroupsInSchoolResult } from "app/auth/queries/getGroupsInSchool"
import type { Group as GroupVO } from "db/client"
import { motion } from "framer-motion"
import { Dialog } from "radix-ui"
import { ReactNode, useEffect, useRef, useState } from "react"
import { MODAL_OVERLAY_Z_INDEX, PX14 } from "theme/consts"
import Checkbox from "ui/Checkbox"
import {
  Content,
  Header,
  Input,
  InputWrapper,
  Root,
  SelectAllWrapper,
} from "ui/FilterSelect"
import Group from "ui/Group"
import Hr from "ui/Hr"
import * as Modal from "ui/Modal"
import RoundedButton from "ui/RoundedButton"
import Stack from "ui/Stack"
import Text from "ui/Text"
import toggleArray from "utils/global/toggleArray"
import { GroupSelectItem } from "./GroupSelectItem"
import { MODAL_Z_INDEX } from "theme/consts"
import { useQueryClient } from "@tanstack/react-query"
import { trpc } from "app/trpc"

export const Overlay = styled(Dialog.Overlay)`
  background-color: rgba(0, 0, 0, 0.5);
  position: fixed;
  inset: 0;
  z-index: ${MODAL_Z_INDEX + 5};
`.withComponent(motion.div)

const ModalRoot = styled(Modal.Root)`
  z-index: ${MODAL_Z_INDEX + 6};
`

type RenderItemProps = {
  group: GetGroupsInSchoolResult[number]
  isActive: boolean
  disabled?: boolean
  onChange: () => void
}

type Props = {
  onClose: () => void
  onSubmit: (groupIds: number[]) => Promise<void>
  getIsDisabled?: (group: GroupVO) => boolean
  children?: ReactNode
  onRenderItem?: (group: RenderItemProps) => ReactNode
  allGroups?: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
}

export default function SelectGroupModal(props: Props) {
  const queryClient = useQueryClient()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const { data: currentGroup } = useSuspenseQuery(
    trpc.users.getCurrentGroup.queryOptions()
  )
  const { data: groups } = useSuspenseQuery(
    trpc.auth.getGroupsInSchool.queryOptions({
      all: props.allGroups || false,
    })
  )
  const [selectedGroups, setSelectedGroups] = useState<number[]>(
    currentGroup ? [currentGroup.id] : []
  )

  useEffect(() => {
    if (currentGroup) {
      setSelectedGroups(currentGroup ? [currentGroup.id] : [])
    }
  }, [currentGroup])

  const [search, setSearch] = useState("")
  const [isLoading, setLoading] = useState(false)

  return (
    <Dialog.Root open={props.open} onOpenChange={props.onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay asChild>
          <Overlay />
        </Dialog.Overlay>
        <Dialog.Content>
          <ModalRoot>
            <Modal.ScrollArea>
              <Modal.Content>
                <Stack gap="24px">
                  {props.children || (
                    <>
                      <Stack gap="16px">
                        <Modal.Title>
                          <Value name="apps.web.src.playlists.components.followplaylisttogroupmodal.title">
                            Zichtbaar voor andere groepen
                          </Value>
                        </Modal.Title>
                        <Hr />
                      </Stack>
                      <Text size={PX14}>
                        <Value name="apps.web.src.playlists.components.followplaylisttogroupmodal.body">
                          {`Selecteer één of meerdere groepen waarvoor je deze lijst
                        wilt opslaan. Duo's uit geselecteerde groepen krijgen
                        deze lijst automatisch te zien.`}
                        </Value>
                      </Text>
                    </>
                  )}
                  <Root>
                    <Header align="center">
                      <SelectAllWrapper>
                        <Checkbox
                          label={
                            <Value
                              name="apps.web.src.themes.components.selectgroupmodal.selecteer_alles"
                              version="1"
                            >
                              Alles
                            </Value>
                          }
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedGroups(groups.map((g) => g.id))
                            } else {
                              setSelectedGroups([])
                            }
                          }}
                        />
                      </SelectAllWrapper>
                      <InputWrapper>
                        <Input
                          // autoFocus
                          inputRef={inputRef}
                          placeholder="Zoek een groep"
                          defaultValue={search || ""}
                          onChange={(e) => {
                            setSearch(e.currentTarget.value)
                          }}
                        />
                      </InputWrapper>
                    </Header>
                    <Content>
                      <Stack>
                        {groups.map((group) => {
                          if (
                            group.displayTitle
                              .toLowerCase()
                              .includes(search.toLowerCase()) === false
                          ) {
                            return null
                          }
                          if (props.onRenderItem) {
                            return props.onRenderItem({
                              group,
                              isActive: selectedGroups.includes(group.id),
                              onChange: () => {
                                setSelectedGroups(toggleArray(group.id))
                              },
                            })
                          }
                          return (
                            <GroupSelectItem
                              key={group.id}
                              isActive={selectedGroups.includes(group.id)}
                              group={group}
                              onChange={() => {
                                setSelectedGroups(toggleArray(group.id))
                              }}
                            />
                          )
                        })}
                      </Stack>
                    </Content>
                  </Root>
                  <Group gap="8px" justify="right">
                    <Dialog.Close asChild>
                      <RoundedButton variant="bordered">
                        <Value name="apps.web.src.groups.components.selectgroupsmodal.annuleren">
                          Annuleren
                        </Value>
                      </RoundedButton>
                    </Dialog.Close>
                    <RoundedButton
                      disabled={isLoading}
                      variant="filled"
                      onClick={async () => {
                        setLoading(true)
                        await props.onSubmit(selectedGroups)
                        await queryClient.invalidateQueries({
                          queryKey:
                            trpc.playlists.getGroupedUserPlaylists.queryKey(),
                        })
                        setLoading(false)

                        props.onClose()
                      }}
                    >
                      <Value name="apps.web.src.themes.components.selectgroupmodal.opslaan">
                        Opslaan
                      </Value>
                    </RoundedButton>
                  </Group>
                </Stack>
                <Dialog.Close asChild>
                  <Modal.Close />
                </Dialog.Close>
              </Modal.Content>
            </Modal.ScrollArea>
          </ModalRoot>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
